import { useContext } from 'react'

import { DateTime } from 'luxon'

import { useForm, useLatestEntity, useRealtimeSubscription } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, generateID, launchModal } from '@domain-group/javascript-utils'

import { baseURL } from '@functions/api'
import { findStatusByKey } from '@functions/status'
import { splitAddress } from '@functions/address'

import useCampaignReconciliationTotals from '@hooks/useCampaignReconciliationTotals'
import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'
import { generateUrls as generateInvoiceUrls } from '@hooks/useInvoice'

import * as campaignQuoteActions from '@redux/modules/campaignQuote'

import defaultFormState, { requiredFields } from '@models/campaignQuote'

import type {
  AgencyPaymentMethodModel, CampaignQuoteModel, CampaignQuoteRequestOptions, ConversationModel,
} from '@models/types'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import PageContext from '@contexts/pageContext'
import RealtimeContext from '@contexts/realtimeContext'
import { getCampaignPermissions, getCampaignType } from 'shared/functions/campaign'
import { CampaignModel } from 'shared/models/campaign'
import { UserModel } from 'shared/models/user'

export type SendQuoteForApprovalPayload = {
  callbacks: {
    afterAction: () => void,
  },
  conversation: Partial<ConversationModel>,
  defaultMessage: string,
}

export type GenerateUrlsOptions = {
  prefixRoute: string,
}

type ArrayElement<T> = T extends (infer U)[] ? U : never;

export type InvalidPrice = ArrayElement<CampaignQuoteModel['invalidItems']> | undefined

export const generateUrls = (
  campaignQuote?: Partial<CampaignQuoteModel>,
  options?: GenerateUrlsOptions,
) => {
  const { id, campaign_id } = campaignQuote || {}
  const { prefixRoute = 'systemManager' } = options || {}

  const campaignContactsModalPayload = encodeURIComponent(
    JSON.stringify({ campaignContact: { campaign_id } }),
  )
  const editCampaignDatesModalPayload = encodeURIComponent(
    JSON.stringify({ campaign: { id: campaign_id } }),
  )

  return {
    approvedQuotesIndexUrl: `#/${prefixRoute}/quotes?status=approved`,
    awaitingReviewQuotesIndexUrl: `#/${prefixRoute}/quotes?status=awaiting_review`,
    campaignEditQuoteUrl: `#/campaigns/${campaign_id}/edit/quotes/${id}/edit`,
    campaignQuotesAddContactUrl:
    `#/campaigns/${campaign_id}/edit/quotes?showModal=CreateCampaignContactModal&showModalPayload=
    ${campaignContactsModalPayload}`,
    campaignQuotesEditDatesUrl:
    `#/campaigns/${campaign_id}/edit/quotes?showModal=EditCampaignDatesModal&showModalPayload=
    ${editCampaignDatesModalPayload}`,
    campaignQuotesIndexUrl: `#/${prefixRoute}/quotes`,
    campaignQuotesOverviewUrl: '#/agency/dashboard/agencies/all/overview/quotes',
    editCampaignQuoteDetailsUrl: `#/${prefixRoute}/quotes/${id}/edit/details`,
    editCampaignQuoteUrl: `#/${prefixRoute}/quotes/${id}/edit`,
    addCampaignQuoteUrl: `#/campaigns/${campaign_id}/edit/quotes?showModal=NewCampaignQuoteModal`,
    pdfDownloadUrl: `${baseURL}/campaign_quotes/${id}/pdf.json`,
    pendingQuotesIndexUrl: `#/${prefixRoute}/quotes?status=pending`,
  }
}

type ApproveCampaignQuoteParams = {
  campaignQuote: Partial<CampaignQuoteModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteRequestOptions,
}

const approveCampaignQuote = (params: ApproveCampaignQuoteParams) => {
  const { campaignQuote, dispatch, requestOptions } = params
  const { approveCampaignQuote: approveFn } = campaignQuoteActions

  // @ts-expect-error TS2345
  return dispatch(approveFn(campaignQuote, requestOptions))
}

type ToggleLockCampaignQuoteParams = {
  data: { payload: CampaignQuoteModel },
  dispatch: AppDispatch,
}

const toggleLockCampaignQuote = (params: ToggleLockCampaignQuoteParams) => {
  const { data: { payload }, dispatch } = params
  const { id, locked } = payload

  const { updateCampaignQuoteKey: updateFn } = campaignQuoteActions

  return dispatch(updateFn({
    // @ts-expect-error TS2322
    campaignQuoteId: id, key: 'locked', value: locked,
  }))
}

type CalculatePaymentMethodFeeParams = {
  agencyPaymentMethod?: AgencyPaymentMethodModel,
  quoteTotal: number,
}

const calculatePaymentMethodFee = (params: CalculatePaymentMethodFeeParams) => {
  const { agencyPaymentMethod, quoteTotal } = params

  if (!agencyPaymentMethod?.id || !agencyPaymentMethod?.fee_value) return null

  const { fee_type, fee_value } = agencyPaymentMethod

  const fee = fee_type === 'fixed' ? fee_value : quoteTotal * (fee_value * 0.01)

  return fee > 0 ? fee : null
}

type CreateCampaignQuoteParams = {
  campaignQuoteParams: Partial<CampaignQuoteModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteRequestOptions,
}

const createCampaignQuote = (params: CreateCampaignQuoteParams) => {
  const { dispatch, campaignQuoteParams, requestOptions } = params
  const { createCampaignQuote: createFn } = campaignQuoteActions

  return dispatch(createFn(campaignQuoteParams, requestOptions))
}

type CreateCampaignQuoteFromExistingParams = {
  campaignQuoteParams: Partial<CampaignQuoteModel>,
  dispatch: AppDispatch,
  existingCampaignQuoteId: number,
  requestOptions?: CampaignQuoteRequestOptions,
}

const createCampaignQuoteFromExisting = (params: CreateCampaignQuoteFromExistingParams) => {
  const {
    campaignQuoteParams, dispatch, existingCampaignQuoteId, requestOptions,
  } = params
  const { createCampaignQuoteFromExisting: createFn } = campaignQuoteActions

  return dispatch(createFn(campaignQuoteParams, existingCampaignQuoteId, requestOptions))
}

type CreateCampaignQuoteFromTemplateParams = {
  campaignQuoteParams: Partial<CampaignQuoteModel>,
  campaignQuoteTemplateId: number,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteRequestOptions,
}

const createCampaignQuoteFromTemplate = (params: CreateCampaignQuoteFromTemplateParams) => {
  const {
    dispatch, campaignQuoteParams, campaignQuoteTemplateId, requestOptions,
  } = params
  const { createCampaignQuoteFromTemplate: createFn } = campaignQuoteActions

  return dispatch(createFn(campaignQuoteParams, campaignQuoteTemplateId, requestOptions))
}

type CreateInvoiceFromCampaignQuoteParams = {
  campaignQuote: Partial<CampaignQuoteModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteRequestOptions,
}

const createInvoiceFromCampaignQuote = (params: CreateInvoiceFromCampaignQuoteParams) => {
  const { campaignQuote, dispatch, requestOptions } = params
  const { createInvoiceFromCampaignQuote: createFn } = campaignQuoteActions

  // @ts-expect-error TS2345
  return dispatch(createFn(campaignQuote, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data, data: { campaign_id, id } } = response

      const { editCampaignInvoiceUrl } = generateInvoiceUrls({ campaign_id, id })

      return {
        success,
        data,
        redirectUrl: editCampaignInvoiceUrl,
      }
    }

    return response
  })
}

type UpdateCampaignQuoteParams = {
  campaignQuote: CampaignQuoteModel,
  campaignQuoteParams: Partial<CampaignQuoteModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteRequestOptions,
}

const updateCampaignQuote = (params: UpdateCampaignQuoteParams) => {
  const {
    dispatch, campaignQuote, campaignQuoteParams, requestOptions,
  } = params
  const { updateCampaignQuote: updateFn } = campaignQuoteActions

  const updatedParams = {
    id: campaignQuote.id,
    ...campaignQuoteParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type BulkUpdateCampaignQuoteItemsPricesParams = {
  campaignQuote: CampaignQuoteModel,
  campaign_quote_item_ids: Array<number>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteRequestOptions,
}

const bulkUpdateCampaignQuoteItemsPrices = (params: BulkUpdateCampaignQuoteItemsPricesParams) => {
  const {
    dispatch, campaignQuote, campaign_quote_item_ids, requestOptions,
  } = params

  const { bulkUpdateCampaignQuoteItemsPrices: bulkFn } = campaignQuoteActions

  return dispatch(bulkFn(campaignQuote, campaign_quote_item_ids, requestOptions))
}

type SortedSectionItemIds = {
  [key: string]: number[],
}

type UpdateQuoteSectionItemOrderParams = {
  campaignQuote: CampaignQuoteModel,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteRequestOptions,
  sortedSectionItemIds: SortedSectionItemIds,
}

const updateCampaignQuoteSectionItemOrder = (params: UpdateQuoteSectionItemOrderParams) => {
  const {
    campaignQuote, dispatch, requestOptions, sortedSectionItemIds,
  } = params
  const { updateCampaignQuoteSectionItemOrder: updateFn } = campaignQuoteActions

  return dispatch(updateFn(campaignQuote, sortedSectionItemIds, requestOptions))
}

type DeleteCampaignQuoteParams = {
  campaignQuote: DeleteParams<CampaignQuoteModel>,
  dispatch: AppDispatch,
}

const deleteCampaignQuote = (params: DeleteCampaignQuoteParams) => {
  const { dispatch, campaignQuote } = params
  const { deleteCampaignQuote: deleteFn } = campaignQuoteActions

  return dispatch(deleteFn(campaignQuote))
}

export type EmailParams = {
  emails: string[],
  message: string,
}

type EmailQuoteParams = {
  campaignQuote: CampaignQuoteModel,
  dispatch: AppDispatch,
  emailParams: EmailParams,
  requestOptions?: CampaignQuoteRequestOptions,
}

const sendQuote = (params: EmailQuoteParams) => {
  const {
    campaignQuote, dispatch, emailParams, requestOptions,
  } = params

  const { sendQuote: sendQuoteFn } = campaignQuoteActions

  return dispatch(sendQuoteFn(campaignQuote.id, emailParams, requestOptions))
}

type ProcessQuoteApprovalParams = {
  callbacks: {
    [key: string]: (payload?: {
        [key: string]: any,
    } | undefined) => void,
  },
  campaign?: CampaignModel,
  campaignQuote: CampaignQuoteModel,
  currentUser?: UserModel,
  startDate?: string,
  dispatch: AppDispatch,
  requestOptions?: object,
  skipPriceValidation?: boolean,
}

const processQuoteApproval = async (params: ProcessQuoteApprovalParams) => {
  const {
    callbacks,
    campaign,
    campaignQuote,
    currentUser,
    startDate,
    dispatch,
    requestOptions,
    skipPriceValidation,
  } = params

  const {
    approveCampaignQuote: approveFn,
    validateCampaignQuotePrices: validateFn,
  } = campaignQuoteActions

  const mergedOptions = {
    ...requestOptions,
    start_date: startDate || null,
  }

  const canManageOrders = getCampaignPermissions({
    // @ts-expect-error TS2339
    currentUser,
    // @ts-expect-error TS2339
    campaignType: getCampaignType(campaign),
  })?.orders?.manage || false

  const prefixUrl = `#/campaigns/${campaignQuote.campaign_id}/edit`

  const cacheKey = generateID()

  const redirectUrl = canManageOrders
    ? `${prefixUrl}/orders/pending?cacheKey=${cacheKey}`
    : `${prefixUrl}/campaignQuotes?cacheKey=${cacheKey}`

  // Validate Prices
  if (!skipPriceValidation){
    const validationResult = await dispatch(validateFn(campaignQuote.id))

    // Invalid Items - Show ConfirmCampaignQuotePricingModal
    if (validationResult.success && validationResult.data?.invalid?.length){
      return new Promise((resolve) => {
        const payload = {
          callbacks: {
            approveCampaignQuote: () => dispatch(approveFn(
              campaignQuote,
              { ...mergedOptions, skipped_price_validation: true },
            )).then((response) => {
              const { success } = response
              if (!success){
                return response
              }

              return {
                ...response,
                redirectUrl,
              }
            }),
          },
          campaignQuote,
          validationErrors: validationResult.data.invalid,
        }

        launchModal({
          callbacks,
          modalKey: 'ConfirmCampaignQuotePricingModal',
          payload,
        })

        resolve({ success: true, result: payload })
      })
    }
  }

  // Approve Quote
  return dispatch(approveFn(campaignQuote, mergedOptions)).then((response) => {
    const { success } = response

    if (!success){
      return response
    }

    return {
      ...response,
      redirectUrl,
    }
  })
}

type ValidateCampaignQuotePrices = {
  campaignQuote: CampaignQuoteModel,
  dispatch: AppDispatch,
}

const validateCampaignQuotePrices = (params: ValidateCampaignQuotePrices) => {
  const { campaignQuote, dispatch } = params
  const { validateCampaignQuotePrices: validateFn } = campaignQuoteActions

  return dispatch(validateFn(campaignQuote.id))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignQuoteForm(
  campaignQuote: Partial<CampaignQuoteModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignQuoteForm = useForm(
    defaultFormState,
    { entity: campaignQuote, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignQuote.id, campaignQuote.cache_key],
  )

  return {
    ...campaignQuoteForm,
  }
}

export const useRelations = (campaignQuote: Partial<CampaignQuoteModel> = {}) => {
  const {
    agency_id,
    agency_payment_method_id,
    campaign_id,
    campaign_quote_template_id,
    status_id,
  } = campaignQuote

  const {
    agencies,
    agencyPaymentMethods,
    campaigns,
    campaignQuoteTemplates,
    paymentMethods,
    statuses,
  } = useSelector(reduxState => reduxState.entities)

  const agency = agency_id && agencies[agency_id] ? agencies[agency_id] : {}
  const agencyPaymentMethod = agency_payment_method_id && agencyPaymentMethods[agency_payment_method_id]
    ? agencyPaymentMethods[agency_payment_method_id]
    : {}
  const campaign = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}
  const campaignQuoteTemplate = campaign_quote_template_id && campaignQuoteTemplates[campaign_quote_template_id]
    ? campaignQuoteTemplates[campaign_quote_template_id]
    : {}
  // @ts-expect-error TS2339
  const paymentMethod = agencyPaymentMethod.payment_method_id && paymentMethods[agencyPaymentMethod.payment_method_id]
    // @ts-expect-error TS2339
    ? paymentMethods[agencyPaymentMethod.payment_method_id]
    : {}
  const status = status_id && statuses[status_id] ? statuses[status_id] : {}

  return {
    agency,
    agencyPaymentMethod,
    campaign,
    campaignQuoteTemplate,
    paymentMethod,
    status,
  }
}

type Options = {
  generateUrlsOptions?: GenerateUrlsOptions,
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteRequestOptions,
  shouldLoadReconciliationTotals?: boolean,
}

function useCampaignQuote(initEntity: Partial<CampaignQuoteModel> = {}, options: Options = {}) {
  const {
    generateUrlsOptions, performHttpRequests, requestOptions, shouldLoadReconciliationTotals,
  } = options

  const currentUserPayload = useCurrentUser()
  const { currentUser } = currentUserPayload

  // @ts-expect-error TS2322
  const { entity: campaignQuote }: { entity: CampaignQuoteModel} = useLatestEntity(initEntity, 'campaignQuotes')
  const {
    agency_id,
    authorized,
    authorization_sent,
    campaign_id,
    id,
    recipient_address,
    total = 0,
    void: isVoid,
  } = campaignQuote

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    agency,
    agencyPaymentMethod,
    campaign,
    campaignQuoteTemplate,
    paymentMethod,
    status,
  } = useRelations(campaignQuote)

  useReduxAction(
    'campaignQuotes',
    'loadCampaignQuote',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: () => !!performHttpRequests,
    },
  )

  useReduxAction(
    'campaignQuotes',
    'validateCampaignQuotePrices',
    {
      campaign_quote_id: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: () => !!performHttpRequests,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignQuotes)
  const { statuses } = useSelector(reduxState => reduxState.entities)

  const reconciliationPayload = useCampaignReconciliationTotals(
    // @ts-expect-error TS2345
    campaign,
    { performHttpRequests: shouldLoadReconciliationTotals },
  )
  const { totals: { outstanding_contributions = 0 } } = reconciliationPayload

  const recipientAddressParts = splitAddress(recipient_address)

  const permissions = getCampaignPermissions({ user: currentUser, campaignType: getCampaignType(campaign) })

  const recalculateEditions = digObject(agency, 'options.recalculate_quote_editions', false)
  const requiresAuthorization = permissions.quotes.require_authorization || false
  const requiresContributions = permissions.quotes.require_contributions || false
  const userCanApproveQuotes = permissions.quotes.approve || false
  const userCanManageQuotes = permissions.quotes.manage || false

  // @ts-expect-error TS2339
  const isApproved = status.key === 'approved'
  // @ts-expect-error TS2339
  const isAwaitingReview = status.key === 'awaiting_review'
  // @ts-expect-error TS2339
  const isPending = status.key === 'pending'

  const isEditable = (isPending || (isAwaitingReview && userCanApproveQuotes))
    && !authorized
    && !authorization_sent
    && !updating

  // @ts-expect-error TS2739
  const paymentMethodFee = calculatePaymentMethodFee({ agencyPaymentMethod, quoteTotal: total })
  const requiresTemplate = digObject(agency, 'options.new_quote_default') === 'template'

  const contributionsDifference = Math.round((total + outstanding_contributions) * 100) / 100
  const contributionsSufficient = requiresContributions ? (contributionsDifference <= 0) : true

  const awaitingReviewStatus = findStatusByKey(statuses, 'awaiting_review')

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  // Realtime/Websocket Connection
  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: { payload: CampaignQuoteModel }) => toggleLockCampaignQuote({ data, dispatch }),
    },
    channelName: 'campaignQuotes',
    clientRef,
    eventName: 'campaignQuote.lockToggled.complete',
  }, [campaignQuote.id])

  return {
    callbacks: {
      addContribution: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignContributionModal',
        payload: {
          campaignContribution: {
            amount: contributionsDifference,
            // @ts-expect-error TS2339
            campaign_id: campaign.id,
            date_received: DateTime.now().toFormat('yyyy-MM-dd'),
            paid_by: 'vendor',
            payment_method: 'EFT',
          },
        },
      }),
      approveCampaignQuote: (entityOptions?: CampaignQuoteRequestOptions) => (
        approveCampaignQuote({ campaignQuote, dispatch, requestOptions: entityOptions })
      ),
      assignRequiredDates: (payload: Record<string, boolean>) => launchModal({
        callbacks,
        modalKey: 'AssignCampaignQuoteItemDatesModal',
        payload: { campaignQuote, ...payload },
      }),
      bulkUpdateInvalidItems: () => launchModal({
        callbacks,
        modalKey: 'BulkUpdateCampaignQuoteInvalidPricesModal',
        payload: {
          campaignQuote,
        },
      }),
      bulkUpdateCampaignQuoteItemsPrices: (
        campaign_quote_item_ids: Array<number>,
        entityOptions?: CampaignQuoteRequestOptions,
      ) => (
        bulkUpdateCampaignQuoteItemsPrices({
          campaignQuote, campaign_quote_item_ids, dispatch, requestOptions: entityOptions,
        })
      ),
      createCampaignQuote: (
        campaignQuoteParams: Partial<CampaignQuoteModel>,
        entityOptions?: CampaignQuoteRequestOptions,
      ) => (
        createCampaignQuote({ campaignQuoteParams, dispatch, requestOptions: entityOptions })
      ),
      createCampaignQuoteFromExisting: (
        campaignQuoteParams: Partial<CampaignQuoteModel>,
        existingCampaignQuoteId: number,
        entityOptions?: CampaignQuoteRequestOptions,
      ) => (
        createCampaignQuoteFromExisting({
          campaignQuoteParams, existingCampaignQuoteId, dispatch, requestOptions: entityOptions,
        })
      ),
      createCampaignQuoteFromTemplate: (
        campaignQuoteParams: Partial<CampaignQuoteModel>,
        campaignQuoteTemplateId: number,
        entityOptions?: CampaignQuoteRequestOptions,
      ) => (
        createCampaignQuoteFromTemplate({
          campaignQuoteParams, campaignQuoteTemplateId, dispatch, requestOptions: entityOptions,
        })
      ),
      createInvoiceFromCampaignQuote: (
        entityOptions?: CampaignQuoteRequestOptions,
      ) => createInvoiceFromCampaignQuote({ campaignQuote, dispatch, requestOptions: entityOptions }),
      createOrEditCampaignQuote: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignQuoteModal',
        payload: { campaignQuote },
      }),
      deleteCampaignQuote: () => deleteCampaignQuote({ dispatch, campaignQuote }),
      editSectionSort: () => launchModal({
        callbacks,
        modalKey: 'EditCampaignQuoteSectionSortModal',
        payload: { campaignQuote },
      }),
      emailCampaignQuote: () => launchModal({
        callbacks,
        modalKey: 'SelectEmailRecipientModal',
        payload: { campaign, campaignQuote },
      }),
      processQuoteApproval: (params?: {
        entityOptions?: CampaignQuoteRequestOptions,
        skipPriceValidation?: boolean,
        startDate?: string,
      }) => (
        processQuoteApproval({
          callbacks,
          // @ts-expect-error TS2339
          campaign,
          campaignQuote,
          // @ts-expect-error TS2339
          currentUser,
          startDate: params?.startDate,
          dispatch,
          requestOptions: {
            include_campaign_quote_campaign_items: true,
            include_campaign_quote_invoices: true,
            include_campaign_quote_orders: true,
            ...params?.entityOptions,
          },
          skipPriceValidation: params?.skipPriceValidation || false,
        })
      ),
      sendQuoteForApproval: () => launchModal({
        callbacks,
        modalKey: 'CreateSubjectConversationModal',
        payload: {
          callbacks: {
            afterAction: () => updateCampaignQuote({
              campaignQuote,
              campaignQuoteParams: {
                // @ts-expect-error TS2339
                status_id: awaitingReviewStatus?.id,
              },
              dispatch,
            }),
          },
          conversation: {
            agency_id,
            campaign_id,
            subject_type: 'CampaignQuote',
            subject_id: campaignQuote.id,
            title: campaignQuote.title,
          },
          defaultMessage: 'Please review and approve quote',
        },
      }),
      sendQuote: (emailParams: EmailParams, entityOptions?: CampaignQuoteRequestOptions) => (
        sendQuote({
          campaignQuote, dispatch, emailParams, requestOptions: entityOptions,
        })
      ),
      updateCampaignQuote: (
        campaignQuoteParams: Partial<CampaignQuoteModel>,
        entityOptions?: CampaignQuoteRequestOptions,
      ) => (
        updateCampaignQuote({
          campaignQuote, campaignQuoteParams, dispatch, requestOptions: entityOptions,
        })
      ),
      updateCampaignQuoteSectionItemOrder: (
        sortedSectionItemIds: SortedSectionItemIds,
        entityOptions?: CampaignQuoteRequestOptions,
      ) => updateCampaignQuoteSectionItemOrder({
        campaignQuote,
        dispatch,
        requestOptions: entityOptions,
        sortedSectionItemIds,
      }),
      validateCampaignQuotePrices: () => validateCampaignQuotePrices({ campaignQuote, dispatch }),
    },
    campaign,
    campaignQuote,
    campaignQuoteTemplate,
    contributionsDifference,
    contributionsSufficient,
    creating,
    currentUserPayload,
    deleting,
    isApproved,
    isAwaitingReview,
    isEditable,
    isPending,
    isVoid,
    loading: loading && !loadedIds.includes(campaignQuote.id),
    paymentMethod,
    paymentMethodFee,
    recalculateEditions,
    recipientAddressParts,
    requiresAuthorization,
    requiresContributions,
    requiresTemplate,
    status,
    updating,
    urls: generateUrls(campaignQuote, generateUrlsOptions),
    userCanApproveQuotes,
    userCanManageQuotes,
  }
}

export type CampaignQuotePayload = ReturnType<typeof useCampaignQuote>

export default useCampaignQuote
